import { createHrefWithSearchParams } from "../createHrefWithSearchParams";
import { separateEncryptableParams } from "../encryption";
import { validateSearchParams } from "../validation";

/**
 * Generates a new HREF with the given base path and search params. In order to specify that a param
 * should be encrypted it should be passed as an object to this function. For example, in order to
 * create a "searchToken" param that is an encrypted string containing a confirmationNumber, firstName,
 * and lastName the following searchParams argument should be provided:
 * {
 *    searchToken: {
 *      confirmationNumber: "ABC123",
 *      firstName: "Donald",
 *      lastName: "Duck"
 *    }
 * }
 * Validations will be performed on the object prior to encryption. If a search param that is meant to be
 * encrypted fails validation then it will be excluded entirely from the resultng HREF rather than
 * be partially encrypted. If any of the encryptable params fail encryption then this function will not
 * include any encrypted params as this could represent a security failure.
 * @param {string} basePath The base of the HREF to create
 * @param {object} searchParams The search parameters that should be included in the HREF
 * @param {object} validations The yup validations to use when generating the HREF. For params that are
 *  to be encrypted the validation should be an object matching the shape of the data. For example, to
 *  validate the searchToken argument provided above the following validations would work:
 *    yup.object({
 *      searchToken: yup.object({
 *        confirmationNumber: yup.string().length(6).required(),
 *        firstName: yup.string().required().min(1),
 *        lastName: yup.string().required().min(2)
 *      })
 *    })
 * @param {EncryptionContext} encryptionContext The context in which to perform encryption. See @swa-ui/encryption for more info
 * @param {string} component The name of the component requesting an ecrypted HREF
 * @param {string} feature - The feature value to determine which encryption method to use
 *    Must be set in jwt-settings in bootstrap data
 * @returns {string} The newly created HREF with validated and encrypted search params
 */
export async function createHrefWithEncryptedSearchParams(
  basePath,
  searchParams,
  validations,
  encryptionContext,
  component,
  feature
) {
  const results = { errors: [], href: "" };
  let paramsToEncode = { ...searchParams };

  if (validations) {
    const { errors: validationErrors, searchParams: validatedSearchParams } = validateSearchParams(
      searchParams,
      validations
    );

    results.errors = validationErrors;
    paramsToEncode = validatedSearchParams;
  }

  const { encryptableParams, simpleParams } = separateEncryptableParams(paramsToEncode);
  const encryptableParamsLength = Object.keys(encryptableParams).length;

  if (encryptableParamsLength > 0) {
    paramsToEncode = { ...simpleParams };

    if (typeof encryptionContext?.encrypt === "function") {
      const encryptedParams = await Promise.all(
        Object.entries(encryptableParams).map(async ([paramName, paramValue]) => {
          const encryptedValue = await encryptionContext.encrypt(paramValue, component, feature);

          return [paramName, encryptedValue];
        })
      );

      if (encryptedParams.length === encryptableParamsLength) {
        const encryptedParamsToEncode = encryptedParams.reduce(
          (currentEncryptedParamsObject, [paramName, encryptedParamValue]) => {
            currentEncryptedParamsObject[paramName] = encryptedParamValue;

            return currentEncryptedParamsObject;
          },
          {}
        );

        paramsToEncode = { ...paramsToEncode, ...encryptedParamsToEncode };
      }
    } else {
      results.errors.push({
        message: "Attempted to encrypt search param without providing a valid encryption context",
        name: "encryption",
        type: "encryption",
      });
    }
  }

  const { href: encodedHref } = createHrefWithSearchParams(basePath, paramsToEncode);

  return {
    errors: results.errors,
    href: encodedHref,
  };
}
